import Vue from "vue";
import { Component } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { IBrand, ICountry, IStatisticsResponse, IRequestStatus, ChartType, Role, RequestStatus,ITheme } from "@/models";
import Datepicker from "vuejs-datepicker";
import * as icons from "@/components/icons";
import PieChart from "./charts/PieChart";
import LineChart from "./charts/LineChart";
import PolarAreaChart from "./charts/PolarAreaChart";

@Component({
    name: "statistics",
    components: {
        "pie-chart": PieChart,
        "line-chart": LineChart,
        "polar-area-chart": PolarAreaChart,
        Multiselect,
        Datepicker,
        "advice-icon": icons.AdviceIcon,
        "formal-compliance-icon": icons.FormalComplianceIcon,
    },
})

export default class Statistics extends Vue {
    public promises: Array<Promise<any>>;

    public filteredRequests: IStatisticsResponse;
    public loadingRequests: boolean;

    public statusPieChart: Chart;
    public typeLineChart: Chart;

    public chartToDisplay: ChartType = 1; // Default on Media

    public responsibleDrinking: boolean;
    public sustainabilityClaim: boolean;
    public nonAlcoholicProduct: boolean;
    public rtd: boolean;

    constructor() {
        super();
        this.promises = [];
        this.loadingRequests = false;
        this.responsibleDrinking = null;
        this.sustainabilityClaim = null;
        this.nonAlcoholicProduct = null;
        this.rtd = null;

        if (!this.$store.state.request.isReferenceDataLoaded) {
            this.promises.push(this.$store.dispatch("request/fetchReferenceData"));
        }

        this.filteredRequests = this.initStatisticsResponse();
        const query: Array<[string, string]> = this.getQueryParameters();
        this.promises.push(this.$store.dispatch("request/getStatistics", query));
    }

    // Getter
    public get nbOfRequests(): number {
        if (this.statsRequests) {
            // tslint:disable-next-line: max-line-length
            return this.statsRequests.requests ? this.statsRequests.requests.filter(req => req.requestStatus.step === 2 && req.requestStatus.name !== RequestStatus.canceled).length : 0;
        } else {
            return 0;
        }
    }

    public get ChartType() {
        return ChartType;
    }

    // Store Access
    public get brands(): IBrand[] {
        return this.$store.getters["request/getBrands"];
    }

    public get countries(): ICountry[] {
        return this.$store.getters["request/getCountries"];
    }
    public get themes(): ITheme[] {
        return this.$store.getters["request/getThemes"];
    }
    public get statuses(): IRequestStatus[] {
        return this.$store.getters["request/getRequestStatus"]
                .map((x: IRequestStatus) => ({
            ...x,
            value: this.$options.filters
                        .displayedStatus(x, Role.Admin)
                        .replace(/^\w/, (c: string) => c.toUpperCase()),
        }))
        // Canceled requests are not include in statistics view
        .filter(r => r.name !== RequestStatus.canceled.toString()) ?? [];
    }

    public get isDisableExportImages(): boolean {
        return !this.loadingRequests && this.nbOfRequests < 500;
    }

    public get statsRequests(): IStatisticsResponse {
        return this.$store.state.request.statisticsRequest;
    }

    public get nbComplaince() {
        return this.$store.getters["request/getNumberOfFormalComplianceRequests"];
    }

    public get nbAdvice() {
        return this.$store.getters["request/getNumberOfAdviceRequests"];
    }

    public get nbValidated() {
        return this.$store.getters["request/getNumberOfValidatedRequests"];
    }

    public get nbRejected() {
        return this.$store.getters["request/getNumberOfRejectedRequests"];
    }

    public get nbPanelWarning() {
        return this.$store.getters["request/getNumberOfMixedRequests"];
    }

    // --------------------------- Media Polar Area Chart ---------------------------
    public get mediaPolarAreaChartData(): Chart.ChartData {
        const chartData: Chart.ChartData = {
            datasets: [{
                data: [],
                backgroundColor: [
                    "rgba(54, 162, 235, 0.2)",
                    "rgba(255, 99, 132, 0.2)",
                    "rgba(255, 216, 10, 0.2)",
                    "rgba(42, 11, 100, 0.2)",
                    "rgba(75, 192, 192, 0.2)",
                    "rgba(153, 102, 255, 0.2)",
                    "rgba(158, 251, 85, 0.2)",
                    "rgba(223, 31, 127, 0.2)",
                    "rgba(204, 204, 204, 0.2)",
                    "rgba(0, 132, 66, 0.2)",
                    "rgba(253, 253, 93, 0.2)",
                    "rgba(255, 159, 64, 0.2)",
                    "rgba(33, 35, 30, 0.2)",
                ],
                borderColor: [
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 99, 132, 1)",
                    "rgba(255, 216, 10, 1)",
                    "rgba(42, 11, 100, 1)",
                    "rgba(75, 192, 192, 1)",
                    "rgba(153, 102, 255, 1)",
                    "rgba(158, 251, 85, 1)",
                    "rgba(223, 31, 127, 1)",
                    "rgba(204, 204, 204, 1)",
                    "rgba(0, 132, 66, 1)",
                    "rgba(253, 253, 93, 1)",
                    "rgba(255, 159, 64, 1)",
                    "rgba(33, 35, 30, 1)",
                ],
                borderWidth: 1,
            }],
            labels: [],
        };

        if (this.statsRequests?.requests) {
            for (const request of this.statsRequests.requests) {
                if (request.media) {
                    for (const media of request.media) {
                        if (chartData.labels.includes(media.value)) {
                            const index = chartData.labels.findIndex(label => label === media.value);
                            chartData.datasets[0].data[index] = chartData.datasets[0].data[index] as number + 1;
                        } else {
                            chartData.datasets[0].data.push(1);
                            chartData.labels.push(media.value);
                        }
                    }
                }
            }
        }

        return chartData;
    }

    public get mediaPolarAreaChartOptions(): Chart.ChartOptions {
       return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                position: "right",
                onHover: e => (e.target as any).style.cursor = "pointer",
                onLeave: e => (e.target as any).style.cursor = "auto",
            },
        };
    }

    // --------------------------- Status Pie Chart ---------------------------
    public get statusPieChartData(): Chart.ChartData {
        const chartData: Chart.ChartData = {
            datasets: [{
                data: [],
                backgroundColor: [
                    "rgba(54, 162, 235, 0.2)",
                    "rgba(255, 99, 132, 0.2)",
                    "rgba(255, 216, 10, 0.2)",
                    "rgba(42, 11, 100, 0.2)",
                    "rgba(75, 192, 192, 0.2)",
                    "rgba(153, 102, 255, 0.2)",
                    "rgba(255, 159, 64, 0.2)",
                    "rgba(223, 31, 127, 0.2)",
                ],
                borderColor: [
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 99, 132, 1)",
                    "rgba(255, 216, 10, 1)",
                    "rgba(42, 11, 100, 1)",
                    "rgba(75, 192, 192, 1)",
                    "rgba(153, 102, 255, 1)",
                    "rgba(255, 159, 64, 1)",
                    "rgba(223, 31, 127, 1)",
                ],
                borderWidth: 1,
            }],
            labels: [],
        };

        if (this.statsRequests?.requests) {
            for (const request of this.statsRequests.requests) {
                // tslint:disable-next-line: max-line-length
                const statusLabel: string = this.$options.filters.displayedStatus(request.requestStatus, Role.Admin).replace(/^\w/, (c: string) => c.toUpperCase());
                if (chartData.labels.includes(statusLabel)) {
                    const index = chartData.labels.findIndex(label => label === request.requestStatus.value);
                    chartData.datasets[0].data[index] = chartData.datasets[0].data[index] as number + 1;
                } else {
                    chartData.datasets[0].data.push(1);
                    chartData.labels.push(statusLabel);
                }
            }
        }

        return chartData;
    }

    public get statusPieChartOptions(): Chart.ChartOptions {
       return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                position: "right",
                onHover: e => (e.target as any).style.cursor = "pointer",
                onLeave: e => (e.target as any).style.cursor = "auto",
            },
        };
    }

    // --------------------------- Type Line Chart ---------------------------
    public get typeLineChartData(): Chart.ChartData {
        const chartData: Chart.ChartData = {
            datasets: [{
                data: [],
                label: "Advice",
                backgroundColor: [
                    "rgba(54, 162, 235, 0.2)",
                ],
                borderColor: [
                    "rgba(54, 162, 235, 1)",
                ],
                borderWidth: 1,
            },
            {
                data: [],
                label: "Formal Compliance",
                backgroundColor: [
                    "rgba(255, 99, 132, 0.2)",
                ],
                borderColor: [
                    "rgba(255, 99, 132, 1)",
                ],
                borderWidth: 1,
            }],
            labels: [],
        };

        if (this.statsRequests?.requests) {
            for (const request of this.statsRequests.requests) {
                const requestMonth = new Date(request.submittedDate).toLocaleString("default", { month: "long", year: "numeric" });

                if (chartData.labels.includes(requestMonth)) {
                    const index = chartData.labels.findIndex(label => label === requestMonth);
                    if (request.advice) {
                        chartData.datasets[0].data[index] = chartData.datasets[0].data[index] as number + 1;
                    } else {
                        chartData.datasets[1].data[index] = chartData.datasets[1].data[index] as number + 1;
                    }
                } else {
                    if (request.advice) {
                        chartData.datasets[0].data.push(1);
                        chartData.datasets[1].data.push(0);
                    } else {
                        chartData.datasets[0].data.push(0);
                        chartData.datasets[1].data.push(1);
                    }
                    chartData.datasets[0].backgroundColor += "rgba(54, 162, 235, 0.2)";
                    chartData.datasets[0].borderColor += "rgba(54, 162, 235, 1)";
                    chartData.datasets[1].backgroundColor += "rgba(255, 99, 132, 0.2)";
                    chartData.datasets[1].borderColor += "rgba(255, 99, 132, 1)";
                    chartData.labels.push(requestMonth);
                }
            }
        }

        return chartData;
    }

    public get typeLineChartOptions(): Chart.ChartOptions {
       return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                position: "right",
                onHover: e => (e.target as any).style.cursor = "pointer",
                onLeave: e => (e.target as any).style.cursor = "auto",
            },
            showLines: true,
            spanGaps: true,
        };
    }

    // Public methods
    public async filterRequests(): Promise<void> {
        this.loadingRequests = true;
        const query: Array<[string, string]> = this.getQueryParameters();
        await this.$store.dispatch("request/getStatistics", query);
        this.loadingRequests = false;
    }

    public async exportRequests(): Promise<void> {
        this.loadingRequests = true;
        const exportResponse: Response = await this.$store.dispatch("request/exportStatistics", this.statsRequests);

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(await exportResponse.blob());
        link.download = "RMP Statistics Export";
        link.click();

        this.loadingRequests = false;
    }

    public async exportDetailledRequests(): Promise<void> {
        this.loadingRequests = true;
        const exportResponse: Response = await this.$store
                                                .dispatch("request/exportDetailledStatistics", this.statsRequests);

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(await exportResponse.blob());
        link.download = "RMP Statistics Export";
        link.click();

        this.loadingRequests = false;
    }

    // Private methods
    private initStatisticsResponse(): IStatisticsResponse {
        return {
            startValidationDate: this.getStartDateCurrentFY(),
            endValidationDate: null,
            brands: [],
            countries: [],
            requestStatuses: [],
            advice: null,
            requests: [],
            themes: [],
        };
    }

    private getStartDateCurrentFY(): Date {
        const currentDate: Date = new Date();

        if (currentDate.getMonth() > 6) {
            return new Date(Date.UTC(currentDate.getFullYear(), 6, 1));
        } else {
            return new Date(Date.UTC(currentDate.getFullYear() - 1, 6, 1));
        }
    }

    private getQueryParameters(): Array<[string, string]> {
        const query: Array<[string, string]> = [];

        if (this.filteredRequests !== null && this.filteredRequests !== undefined) {
            if (this.filteredRequests.startValidationDate !== null &&
            this.filteredRequests.startValidationDate !== undefined) {
                query.push(["startValidationDate", String(this.filteredRequests.startValidationDate.toISOString())]);
            }

            if (this.filteredRequests.endValidationDate !== null &&
                this.filteredRequests.endValidationDate !== undefined) {
                query.push(["endValidationDate", String(this.filteredRequests.endValidationDate.toISOString())]);
            }

            if (this.filteredRequests.brands !== null &&
                this.filteredRequests.brands !== undefined &&
                this.filteredRequests.brands.length > 0) {
                for (const brand of this.filteredRequests.brands) {
                    query.push(["brandIds", String(brand.brandId)]);
                }
            }

            if (this.filteredRequests.themes !== null &&
                this.filteredRequests.themes !== undefined &&
                this.filteredRequests.themes.length > 0) {
                for (const theme of this.filteredRequests.themes) {
                    query.push(["themeIds", String(theme.themeId)]);
                }
            }

            if (this.filteredRequests.countries !== null &&
                this.filteredRequests.countries !== undefined &&
                this.filteredRequests.countries.length > 0) {
                for (const country of this.filteredRequests.countries) {
                    query.push(["countryIds", String(country.countryId)]);
                }
            }

            if (this.filteredRequests.requestStatuses !== null &&
                this.filteredRequests.requestStatuses !== undefined) {
                for (const status of this.filteredRequests.requestStatuses) {
                    query.push(["requestStatusIds", String(status.requestStatusId)]);
                }
            }

            if (this.filteredRequests.advice !== null && this.filteredRequests.advice !== undefined) {
                query.push(["type", String(this.filteredRequests.advice)]);
            }

            if (this.responsibleDrinking != null) {
                query.push(["responsibleDrinking", String(this.responsibleDrinking)]);
            }

            if (this.sustainabilityClaim != null) {
                query.push(["sustainabilityClaim", String(this.sustainabilityClaim)]);
            }

            if (this.nonAlcoholicProduct != null) {
                query.push(["nonAlcoholicProduct", String(this.nonAlcoholicProduct)]);
            }

            if (this.rtd != null) {
                query.push(["rtd", String(this.rtd)]);
            }
        } else {
            // First loading of the page
            query.push(["startValidationDate", String(this.getStartDateCurrentFY().toISOString())]);
        }

        return query;
    }
}
